import { Spinner } from '@components/core-ui/spinners/Spinner';
import { redirectBrowserTo } from '@helpers/redirectBrowserTo';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useMutation } from '@tanstack/react-query';
import type { SsoLoginBodyDto } from 'app/API';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { ssoLogin } from './api/ssoLogin';
import { clearSsoProviderSession } from './helpers/clearSsoProviderSession';
import { getDesktopHandoff } from './helpers/getDesktopHandoff';
import { getSafeSsoContinuation } from './helpers/getSafeSsoContinuation';
import { getSafeSsoLogoutUrl } from './helpers/getSafeSsoLogoutUrl';
import { markSsoProviderSession } from './helpers/markSsoProviderSession';

const fallbackError = 'We could not complete your company login. Please try again.';

const getResponseError = (response: unknown) => {
  if (!response || typeof response !== 'object') {
    return fallbackError;
  }

  if ('message' in response && typeof response.message === 'string') {
    return response.message;
  }

  if ('code' in response && typeof response.code === 'string') {
    return response.code;
  }

  if (!('userId' in response) || typeof response.userId !== 'string' || !response.userId) {
    return fallbackError;
  }

  return '';
};

export const SSOLogin = () => {
  const { call } = useAPI();
  const { mutateAsync: exchangeSsoCode } = useMutation({
    mutationFn: async (requestBody: SsoLoginBodyDto) => {
      let requestError = '';

      try {
        const response = await ssoLogin({
          call,
          onError: (message) => {
            requestError = message;
          },
          requestBody,
        });

        return requestError || getResponseError(response);
      } catch {
        throw new Error(requestError || fallbackError);
      }
    },
  });
  const { mutateAsync: markProviderSession } = useMutation({ mutationFn: markSsoProviderSession });
  const { mutateAsync: clearProviderSession } = useMutation({ mutationFn: clearSsoProviderSession });
  const hasHandledCallback = useRef(false);
  const [error, setError] = useState('');
  const [logoutUrl, setLogoutUrl] = useState('');

  useEffect(() => {
    if (hasHandledCallback.current) {
      return;
    }

    hasHandledCallback.current = true;
    const searchParams = new URLSearchParams(window.location.search);
    const expectedState = window.sessionStorage.getItem('sso.oauthState');
    const returnedState = searchParams.get('state');
    const continuation = getSafeSsoContinuation(window.sessionStorage.getItem('sso.continuation'));
    const storedLogoutUrl = window.sessionStorage.getItem('sso.logoutUrl');
    const desktopHandoff = getDesktopHandoff(continuation);
    window.sessionStorage.removeItem('sso.oauthState');
    window.sessionStorage.removeItem('sso.continuation');
    window.sessionStorage.removeItem('sso.logoutUrl');

    if (!expectedState || !returnedState || expectedState !== returnedState) {
      setError('We could not verify this company login request. Please start the login again.');
      return;
    }

    const completeLogin = async () => {
      if (!desktopHandoff) {
        await markProviderSession().catch(() => undefined);
      }

      const safeLogoutUrl = getSafeSsoLogoutUrl(storedLogoutUrl);
      const providerError = searchParams.get('error');
      if (providerError) {
        setError(searchParams.get('error_description') || `Company login failed: ${providerError}`);
        setLogoutUrl(safeLogoutUrl || '');
        return;
      }

      const code = searchParams.get('code');
      if (!code) {
        setError('The company login response did not include an authorization code. Please try again.');
        return;
      }

      const requestBody = desktopHandoff
        ? { code, handoff: desktopHandoff, state: returnedState }
        : { code, state: returnedState };
      let loginError: string;

      try {
        loginError = await exchangeSsoCode(requestBody);
      } catch (exchangeError) {
        loginError = exchangeError instanceof Error ? exchangeError.message : fallbackError;
      }

      if (loginError) {
        setError(loginError);
        setLogoutUrl(safeLogoutUrl || '');
        return;
      }

      redirectBrowserTo(continuation);
    };

    void completeLogin();
  }, [exchangeSsoCode, markProviderSession]);

  const handleAnotherAccount = (evt: MouseEvent<HTMLAnchorElement>) => {
    if (!logoutUrl) {
      return;
    }

    evt.preventDefault();
    void clearProviderSession()
      .catch(() => undefined)
      .finally(() => redirectBrowserTo(logoutUrl));
  };

  return (
    <main className="flex min-h-[360px] w-full items-center justify-center px-6 text-center">
      {error ? (
        <div className="max-w-md">
          <h1 className="mb-4 text-2xl font-semibold text-title">Company login failed</h1>
          <p className="mb-6 text-danger" data-testid="sso-login-error" role="alert">
            {error}
          </p>
          <a
            className="text-link underline hover:no-underline"
            href={logoutUrl || routes.signin.make()}
            onClick={handleAnotherAccount}
          >
            Sign in with another account
          </a>
        </div>
      ) : (
        <div aria-live="polite" className="relative flex min-h-40 w-full max-w-md items-center justify-center">
          <Spinner className="inset-0" />
          <p className="mt-28 text-foreground-muted">Completing your company login…</p>
        </div>
      )}
    </main>
  );
};
